import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { HitApi } from '../../../Api/ApiHit';
import { addMaintenanceMode, updateMaintenanceMode, searchMaintenanceMode } from '../../../constant/Constant';
import { ArrowLeft, Save } from 'lucide-react';
import toast from 'react-hot-toast';

const AddMaintenanceMode = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '',
    message: '',
    isEnabled: false,
    startTime: '',
    endTime: '',
    allowedRoles: '',
    enabledBy: '',
    isActive: true
  });

  useEffect(() => {
    if (isEdit) {
      fetchRecord();
    }
  }, [id]);

  const fetchRecord = () => {
    setLoading(true);
    const json = {
      page: 1,
      limit: 1,
      search: id
    };
    HitApi(json, searchMaintenanceMode)
      .then((res) => {
        const doc = res?.data?.docs?.[0];
        if (doc) {
          setForm({
            title: doc.title || '',
            message: doc.message || '',
            isEnabled: doc.isEnabled || false,
            startTime: doc.startTime ? new Date(doc.startTime).toISOString().slice(0, 16) : '',
            endTime: doc.endTime ? new Date(doc.endTime).toISOString().slice(0, 16) : '',
            allowedRoles: doc.allowedRoles?.join(', ') || '',
            enabledBy: doc.enabledBy || '',
            isActive: doc.isActive !== false
          });
        } else {
          toast.error('Record not found');
          navigate('/app-control/maintenance');
        }
      })
      .catch(() => {
        toast.error('Failed to fetch record');
        navigate('/app-control/maintenance');
      })
      .finally(() => setLoading(false));
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!form.message.trim()) {
      toast.error('Message is required');
      return;
    }

    setSaving(true);

    const payload = {
      title: form.title.trim(),
      message: form.message.trim(),
      isEnabled: form.isEnabled,
      startTime: form.startTime ? new Date(form.startTime).toISOString() : null,
      endTime: form.endTime ? new Date(form.endTime).toISOString() : null,
      allowedRoles: form.allowedRoles ? form.allowedRoles.split(',').map((r) => r.trim()).filter(Boolean) : [],
      enabledBy: form.enabledBy.trim(),
      isActive: form.isActive
    };

    if (isEdit) {
      payload._id = id;
    }

    const api = isEdit ? updateMaintenanceMode : addMaintenanceMode;

    HitApi(payload, api)
      .then((res) => {
        if (res?.statusCode === 200 || res?.statusCode === 201 || res?.success) {
          toast.success(`Maintenance mode ${isEdit ? 'updated' : 'created'} successfully`);
          navigate('/app-control/maintenance');
        } else {
          toast.error(res?.message || `Failed to ${isEdit ? 'update' : 'create'}`);
        }
      })
      .catch(() => toast.error(`Failed to ${isEdit ? 'update' : 'create'} maintenance mode`))
      .finally(() => setSaving(false));
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate('/app-control/maintenance')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEdit ? 'Edit Maintenance Mode' : 'Add Maintenance Mode'}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {isEdit ? 'Update maintenance window settings' : 'Configure a new maintenance window'}
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow border border-gray-100 p-6 max-w-2xl">
        {/* Title */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => handleChange('title', e.target.value)}
            placeholder="e.g. Scheduled Maintenance"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
          />
        </div>

        {/* Message */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
          <textarea
            value={form.message}
            onChange={(e) => handleChange('message', e.target.value)}
            placeholder="e.g. We are currently performing scheduled maintenance. Please try again later."
            rows={4}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent resize-none"
          />
        </div>

        {/* Start & End Time */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
            <input
              type="datetime-local"
              value={form.startTime}
              onChange={(e) => handleChange('startTime', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
            <input
              type="datetime-local"
              value={form.endTime}
              onChange={(e) => handleChange('endTime', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
            />
          </div>
        </div>

        {/* Enabled By */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Enabled By</label>
          <input
            type="text"
            value={form.enabledBy}
            onChange={(e) => handleChange('enabledBy', e.target.value)}
            placeholder="e.g. Admin name"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
          />
        </div>

        {/* Allowed Roles */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Allowed Roles</label>
          <input
            type="text"
            value={form.allowedRoles}
            onChange={(e) => handleChange('allowedRoles', e.target.value)}
            placeholder="Comma separated, e.g. admin, superadmin"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
          />
          <p className="text-xs text-gray-400 mt-1">Roles that can bypass maintenance mode</p>
        </div>

        {/* Toggles */}
        <div className="flex items-center gap-6 mb-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.isEnabled}
              onChange={(e) => handleChange('isEnabled', e.target.checked)}
              className="w-4 h-4 accent-black"
            />
            <span className="text-sm text-gray-700">Enable Maintenance Now</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => handleChange('isActive', e.target.checked)}
              className="w-4 h-4 accent-black"
            />
            <span className="text-sm text-gray-700">Active Record</span>
          </label>
        </div>

        {/* Submit */}
        <div className="flex gap-3">
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex items-center gap-2 bg-black text-white px-6 py-2 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
          </button>
          <button
            onClick={() => navigate('/app-control/maintenance')}
            className="px-6 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddMaintenanceMode;
